import { UseMutationOptions, useQueryClient } from '@tanstack/react-query';

import useSetCustomerBillingAddress from '@/api/mutations/customers/useSetCustomerBillingAddress';
import customerKeys from '@/api/queries/customers/keyFactory';
import {
  CustomerQuery,
  SetCustomerBillingAddressInput,
  SetCustomerBillingAddressMutation as _SetCustomerBillingAddressMutation,
} from '@/gql/graphql';

const useOptimisticSetCustomerBillingAddress = (
  customerId: string,
  options?: UseMutationOptions<
    _SetCustomerBillingAddressMutation,
    unknown,
    SetCustomerBillingAddressInput
  >
) => {
  const queryClient = useQueryClient();

  return useSetCustomerBillingAddress({
    ...options,
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: customerKeys.customer(customerId),
      });

      // Snapshot the previous value
      const previousCustomer = queryClient.getQueryData(
        customerKeys.customer(customerId)
      );

      // Optimistically update to the new value
      queryClient.setQueryData<CustomerQuery>(
        customerKeys.customer(customerId),
        (old) => {
          if (!old) return;

          return {
            ...old,
            customer: {
              ...old.customer,
              billingAddress: old.customer.addresses.find(
                (address) => address.id === variables.addressId
              ),
            },
          };
        }
      );

      options?.onMutate?.(variables);

      // Return a context object with the snapshotted value
      return { previousCustomer };
    },
    onError: (err, newTodo, context) => {
      // If an error happens, rollback!
      queryClient.setQueryData(
        customerKeys.customer(customerId),
        // @ts-expect-error
        context.previousCustomer
      );

      options?.onError?.(err, newTodo, context);
    },
    // Always refetch after error or success:
    onSettled: (...args) => {
      // oxlint-disable-next-line typescript-eslint(no-floating-promises) -- FIXME: pre-existing error: Promises must be awaited.
      queryClient.invalidateQueries({
        queryKey: customerKeys.customer(customerId),
      });

      options?.onSettled?.(...args);
    },
  });
};

export default useOptimisticSetCustomerBillingAddress;
